import store from "@/store/index";
import { getProfile as getProfileApi } from "@/apis/auth";
import { getTaskStatus as getTaskStatusApi } from "@/apis/common";
import moment from "moment";
import { POSITION_OFFSET, TEAM_JOB_TITLES, USER_ROLES } from "./constants/index";
import { postAttachments } from "@/apis/common/index";

// A corrected timer's *total* must land on a multiple of `step` minutes -
// the backend enforces this on the final value, not on the change amount -
// so nudging a non-multiple recording (e.g. 11m) with +5 must still land
// clean (15, not 16). Used for both typed input and the +/-step buttons.
export function snapCorrectionMinutes(value, step = 5) {
  return Math.max(0, Math.round((value || 0) / step) * step);
}

export function formatDueDate(dueDate) {
  if (dueDate === null) {
    return "N/A";
  }
  return moment(dueDate, "YYYY-MM-DD").format("DD/MM/YY");
}
export function ellipsify(string, charCount = 15) {
  if (typeof string !== "string") {
    return "";
  }
  if (string.length >= charCount) {
    return string.substring(0, charCount - 3) + "...";
  }
  return string;
}
export function isImage(attachment) {
  const fileTypes = ["png", "jpg", "jpeg", "gif"];
  // attachments which are have been saved in the backend
  if (attachment.attachment) {
    return fileTypes.includes(attachment.attachment.filetype.toLowerCase());
  }
  // attachments just uploaded by the user
  return fileTypes.some(fileType => attachment.type === `image/${fileType}`);
}
export function isFile(attachment) {
  return attachment instanceof File;
}
export function getFileExtension(attachment) {
  let tokens = attachment.name.split(".");
  return tokens[tokens.length - 1].toUpperCase();
}

export function notificationNavigator(id, action) {
  if (action === "handover-reassigned") {
    this.$router.push({
      name: "ViewTask",
      params: {
        id: id
      }
    });
  } else if (action === "assign-assistant-to-customer") {
    this.$router.push({
      name: "ClientIntake",
      params: {
        profileId: id
      }
    });
  } else if (action === "handover-request-made") {
    if (this.$route.name === "HandoverTasks" && this.$route.params.tab === 0) {
      this.$router.go();
    } else {
      this.$router.push({
        name: "HandoverTasks",
        params: {
          tab: 0
        }
      });
    }
  } else if (action === "handover-request-declined") {
    if (this.$route.name === "HandoverTasks" && this.$route.params.tab === 1) {
      this.$router.go();
    } else {
      this.$router.push({
        name: "HandoverTasks",
        params: {
          tab: 1
        }
      });
    }
  }
}

export function isAdmin() {
  return store.state.authModule.role === "admin";
}
export function getNewPosition(positionInfo) {
  let { previousPosition, nextPosition } = positionInfo;
  let result;

  // the case when the receiving queue is empty
  if (previousPosition === null && nextPosition === null) {
    return POSITION_OFFSET;
  } else if (previousPosition == null) {
    // card is moved to the top of a queue
    result = parseFloat(nextPosition) / 2;
  } else if (nextPosition === null) {
    // card is moved to the bottom of a queue
    result = parseFloat(previousPosition) + POSITION_OFFSET;
  } else {
    result = (parseFloat(previousPosition) + parseFloat(nextPosition)) / 2;
  }
  return parseFloat(result.toFixed(7));
}
export function uploadFile(file) {
  const { profileUserId } = store.state.authModule;
  let myFile = new File([file], file.name);
  const formData = new FormData();
  formData.append("owner", profileUserId);
  formData.append("attachmentFile", myFile);
  return postAttachments(formData);
}
export function getInitials(name) {
  return name.match(/\b(\w)/g).join("");
}
export function getLoggedInUser() {
  return store.state.authModule;
}
export function formatDuration(duration) {
  let seconds = duration._data.seconds;
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  let minutes = duration._data.minutes;
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  let hours = duration._data.hours + duration._data.days * 24;
  if (hours < 10) {
    hours = `0${hours}`;
  }
  return `${hours}:${minutes}:${seconds}`;
}

export function beforeRouteLeave(to, from, next) {
  if (!this.dirty || to.name === "Login") {
    next();
  } else {
    const answer = window.confirm(
      "Do you really want to leave? you have unsaved changes!"
    );
    if (answer) {
      next();
    } else {
      next(false);
    }
  }
}

export function dirty(isDirty) {
  if (this.saved) {
    return false;
  }
  const isListenerAttached = this.$store.state.isUnloadListenerAttached;
  if (isDirty && !isListenerAttached) {
    this.$store.commit({
      type: "attachUnloadListener",
      onBeforeUnload: this.onBeforeUnload
    });
  }
  if (!isDirty) {
    this.$store.commit({
      type: "removeUnloadListener",
      onBeforeUnload: this.onBeforeUnload
    });
  }
}

export function isEmpty(value) {
  return value === "" || value === null || value === undefined;
}

export function validateDates(from, to) {
  let regex = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;

  const datesAreEmpty = isEmpty(from) && isEmpty(to);
  if (datesAreEmpty) return true;

  const datesValid = regex.test(from) && regex.test(to);
  if (!datesValid) return false;

  let _from = moment(from, "YYYY-MM-DD");
  let _to = moment(to, "YYYY-MM-DD");

  // no dates is a valid filter
  // if dates are applied, then both have to be filled and valid
  let isValid = _from.isSameOrBefore(_to);
  return isValid;
}

export async function getProfile() {
  if (store.state.me !== undefined) {
    return store.state.me;
  }
  let apiReponse = await getProfileApi();
  store.state.me = apiReponse;
  return store.state.me;
}

export async function getTaskStatus() {
  if (store.state.statuses !== undefined) {
    return store.state.statuses;
  }
  store.state.statuses = await getTaskStatusApi();
  return store.state.statuses;
}

/**
 * @description - This function is used get fullname of Job Title
 * @param {string} shortName - Short name/Value of Job Title e.g. EA_US
 * @returns {string} - Full name of Job Title e.g. Assistant US
 */
export function getAssistantJobTitle(shortName) {
  let jobTitle = TEAM_JOB_TITLES.filter(title => title.value === shortName);
  return jobTitle[0].text;
}

/**
 * @description - Whether the signed-in user holds the Owner role. Reads the
 * store first and falls back to localStorage so it is also usable from
 * router guards, which run before the store is rehydrated.
 * @returns {boolean}
 */
export function isOwner() {
  const role =
    (store.state.authModule && store.state.authModule.role) ||
    localStorage.getItem("user-role");
  return role === USER_ROLES.OWNER;
}

export function getInitialsFromName(name) {
  const parts = name.split(" ");
  const hasOnlyOnePart = parts.length === 1;

  if (hasOnlyOnePart) {
    return parts[0][0] + parts[0][1];
  }
  return parts[0][0] + parts[1][0];
}
